import { t } from '@/i18n/t';
import { ApiTicket, ApiComment } from '@/services/tickets.service';

export interface Ticket {
  id: string;
  status: "new" | "open" | "pending" | "in_progress" | "solved" | "closed";
  subject: string;
  requester: {
    name: string;
    email: string;
    avatar?: string;
  };
  lastContact: {
    sender: "customer" | "agent";
    time: string;
    timestamp?: string;
  };
  updatedAt: string;
  createdAt: string;
  dueDate?: string;
  owner?: {
    name: string;
    avatar?: string;
  };
  ownerId?: string | null;
  watchers?: string[];
  shopType?: "shop1.de" | "shop2.de" | "shop3.de";
  unread?: boolean;
  slaAlert?: boolean;
  messageCount: number;
  ticketNumber: string;
  referenceId: string;
  priority: "low" | "normal" | "high" | "urgent";
  internalTargetOrgId?: string | null;
  internalTargetOrgName?: string | null;
  organization: {
    id: string;
    name: string;
  };
  sla?: {
    policyName?: string;
    firstResponseTarget?: string;
    firstResponseAt?: string;
    firstResponseStatus?: "pending" | "achieved" | "breached";
    resolutionTarget?: string;
    resolutionAt?: string;
    resolutionStatus?: "pending" | "achieved" | "breached";
    isPaused?: boolean;
  };
  isSpam?: boolean | null;
  requester_id: string;
  mergedTicketCount?: number;
  mergedTicketNumbers?: string[] | null;
  // Parent ticket information (if this is a merged child ticket)
  parent_ticket_id?: string | null;
  parent_ticket_id_actual?: string | null;
  parent_ticket_number?: string | null;
  parent_ticket_subject?: string | null;
  parent_ticket_status?: string | null;
  tags?: string[];
  channel?: string | null;
  // AI Ticket Processor fields
  ai_flag?: 'green' | 'yellow' | 'red' | null;
  ai_category?: string | null;
  ai_sentiment_score?: number | null;
  ai_processed_at?: string | null;
}

export interface Comment {
  id: string;
  content: string;
  author: {
    name: string;
    email: string;
    avatar?: string;
  };
  timestamp: string;
  isInternal: boolean;
  isEmailComment: boolean;
  emailSourceType?: string;
  hasAttachments: boolean;
  attachmentCount: number;
  attachmentsUrl?: string;
  sender: "customer" | "agent";
}

const shopTypes: ("shop1.de" | "shop2.de" | "shop3.de")[] = [
  "shop1.de",
  "shop2.de", 
  "shop3.de"
];

const lastContactSenders = [
  "Support Team",
  "System",
  "Agent",
  "Auto Reply"
];

// Convert API status to frontend status
export const mapApiStatusToFrontend = (apiStatus: string): Ticket["status"] => {
  switch (apiStatus) {
    case 'new':
      return 'new';
    case 'open':
      return 'open';
    case 'pending':
      return 'pending';
    case 'in_progress':
      return 'in_progress';
    case 'solved':
      return 'solved';
    case 'closed':
      return 'closed';
    default:
      return 'new';
  }
};

// Convert frontend status to API status
export const mapFrontendStatusToApi = (frontendStatus: Ticket["status"]): string => {
  switch (frontendStatus) {
    case 'new':
      return 'new';
    case 'open':
      return 'open';
    case 'pending':
      return 'pending';
    case 'in_progress':
      return 'in_progress';
    case 'solved':
      return 'solved';
    case 'closed':
      return 'closed';
    default:
      return 'open';
  }
};

// Generate avatar URL based on name
const generateAvatar = (name: string): string => {
  const cleanName = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${cleanName}`;
};

// Generate dummy data for fields not in API
const generateDummyData = (ticketId: string) => {
  const hash = ticketId.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  // Use hash to generate consistent random values for each ticket
  const random1 = Math.abs(hash) % 100;
  const random2 = Math.abs(hash * 31) % 100;
  const random3 = Math.abs(hash * 17) % 100;
  
  return {
    shopType: random1 > 70 ? shopTypes[random1 % 3] : undefined,
    unread: random2 > 75,
    slaAlert: random3 > 90,
    lastContactSender: lastContactSenders[random1 % lastContactSenders.length],
    lastContactTime: `${(random2 % 72) + 1} Stunden`,
    dueDate: random3 > 60 ? new Date(Date.now() + (random2 % 7) * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : undefined
  };
};

// Calculate relative time
const calculateRelativeTime = (timestamp: string): string => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInHours < 1) {
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    return t("common:relative.minutes", "vor {{count}} Min.", { count: diffInMinutes });
  } else if (diffInHours < 24) {
    return t("common:relative.hours", "vor {{count}} Stunden", { count: diffInHours });
  } else {
    // Plural-Schlüssel deckt den Sonderfall "vor 1 Tag" ab (days_one)
    return t("common:relative.days", "vor {{count}} Tagen", { count: diffInDays });
  }
};

// Transform API ticket to frontend ticket format
export const transformApiTicketToFrontend = (apiTicket: ApiTicket): Ticket => {
  const requesterName = `${apiTicket.requester_first_name} ${apiTicket.requester_last_name}`.trim();
  const ownerName = apiTicket.assignee_first_name && apiTicket.assignee_last_name
    ? `${apiTicket.assignee_first_name} ${apiTicket.assignee_last_name}`.trim()
    : null;

  const dummyData = generateDummyData(apiTicket.id);

  // Check for SLA breach to set slaAlert
  const hasSlaBreached = (apiTicket as any).first_response_status === 'breached' ||
                        (apiTicket as any).resolution_status === 'breached';

  // Determine last contact info from API data or fallback to dummy
  let lastContactSender: "customer" | "agent" = "agent";
  let lastContactTime = dummyData.lastContactTime;
  let lastContactTimestamp = apiTicket.updated_at;

  // If we have last_comment info from API, use it
  if ((apiTicket as any).last_comment_author_role) {
    const lastCommentRole = (apiTicket as any).last_comment_author_role;
    lastContactSender = lastCommentRole === 'customer' ? 'customer' : 'agent';

    if ((apiTicket as any).last_comment_created_at) {
      lastContactTimestamp = (apiTicket as any).last_comment_created_at;
      lastContactTime = calculateRelativeTime(lastContactTimestamp);
    }
  } else {
    // Fallback: if requester is different from assignee, assume last contact from customer
    // Otherwise use agent as fallback
    lastContactSender = Math.random() > 0.5 ? "customer" : "agent";
    lastContactTime = calculateRelativeTime(apiTicket.updated_at);
  }

  return {
    id: apiTicket.id,
    ticketNumber: apiTicket.ticket_number,
    referenceId: (apiTicket as any).reference_id || '',
    status: mapApiStatusToFrontend(apiTicket.status),
    subject: apiTicket.subject,
    priority: apiTicket.priority,
    requester_id: apiTicket.requester_id,
    requester: {
      name: requesterName || 'Unknown User',
      email: apiTicket.requester_email,
      avatar: generateAvatar(requesterName || 'unknown')
    },
    lastContact: {
      sender: lastContactSender,
      time: lastContactTime,
      timestamp: lastContactTimestamp
    },
    updatedAt: apiTicket.updated_at,
    createdAt: apiTicket.created_at,
    dueDate: (apiTicket as any).next_reminder_date || undefined,
    owner: ownerName ? {
      name: ownerName,
      avatar: generateAvatar(ownerName)
    } : undefined,
    ownerId: apiTicket.assignee_id,
    watchers: Array.isArray((apiTicket as any).watchers) ? (apiTicket as any).watchers : [],
    shopType: dummyData.shopType,
    unread: dummyData.unread,
    slaAlert: hasSlaBreached || dummyData.slaAlert,
    messageCount: parseInt(apiTicket.comment_count) || 0,
    internalTargetOrgId: (apiTicket as any).internal_target_org_id || null,
    internalTargetOrgName: (apiTicket as any).internal_target_org_name || null,
    organization: {
      id: apiTicket.organization_id,
      name: apiTicket.organization_name
    },
    sla: (apiTicket as any).sla_policy_name ? {
      policyName: (apiTicket as any).sla_policy_name,
      firstResponseTarget: (apiTicket as any).first_response_target,
      firstResponseAt: (apiTicket as any).first_response_at,
      firstResponseStatus: (apiTicket as any).first_response_status,
      resolutionTarget: (apiTicket as any).resolution_target,
      resolutionAt: (apiTicket as any).resolution_at,
      resolutionStatus: (apiTicket as any).resolution_status,
      isPaused: (apiTicket as any).sla_paused
    } : undefined,
    isSpam: apiTicket.is_spam,
    mergedTicketCount: apiTicket.merged_ticket_count,
    mergedTicketNumbers: apiTicket.merged_ticket_numbers,
    // Parent ticket information (if this is a merged child ticket)
    parent_ticket_id: (apiTicket as any).parent_ticket_id,
    parent_ticket_id_actual: (apiTicket as any).parent_ticket_id_actual,
    parent_ticket_number: (apiTicket as any).parent_ticket_number,
    parent_ticket_subject: (apiTicket as any).parent_ticket_subject,
    parent_ticket_status: (apiTicket as any).parent_ticket_status,
    // Tags
    tags: (apiTicket as any).tags || [],
    // Channel (source)
    channel: (apiTicket as any).channel || null,
    // AI Ticket Processor fields
    ai_flag: (apiTicket as any).ai_flag || null,
    ai_category: (apiTicket as any).ai_category || null,
    ai_sentiment_score: (apiTicket as any).ai_sentiment_score ?? null,
    ai_processed_at: (apiTicket as any).ai_processed_at || null
  };
};

// Clean email content by removing email thread/reply parts
const cleanEmailContent = (content: string): string => {
  if (!content) return content;

  // HTML içerik: metin-regex'leri ASLA çalıştırma — bir e-posta HTML'i tek satırda olabilir
  // ve "Am besagten... schrieb am ...:" gibi cümleler quote-header'ı sanılıp GERÇEK mesajı siler
  // (ör. müşteri "Am besagten Heizkörper..." yazınca tüm mesaj kayboluyordu). Bunun yerine
  // alıntı (quote) container'ını YAPISAL olarak kaldırırız — güvenli, içerik düşmez.
  const isHtml = /<(div|p|br|blockquote|table|span|a|img)\b/i.test(content);
  if (isHtml) {
    let c = content;
    // Gmail: <div class="gmail_quote ...">…alıntı…</div> → o noktadan sonrasını at
    c = c.replace(/<div[^>]*class="[^"]*gmail_quote[^"]*"[\s\S]*$/i, '');
    // Apple Mail / genel: alıntı blockquote'u ve sonrasını at
    c = c.replace(/<blockquote[\s\S]*$/i, '');
    // Outlook: "Von:/From:" başlıklı bölünmüş yanıt bloğu
    c = c.replace(/<div[^>]*id=["']?(divRplyFwdMsg|mail-editor-reference-message-container)["']?[\s\S]*$/i, '');
    return c.trim();
  }

  // Büyük düz metin → dokunma (catastrophic backtracking önlemi)
  if (content.length > 10000) return content;

  // Düz METİN için satır-tabanlı alıntı temizliği (HTML'e uygulanmaz)
  const patterns = [
    // Handle "On ... at ... wrote:" patterns with potential line breaks
    /\s*On\s+[^<\n]*\s+at\s+[^<\n]*\s+[^<\n]*\s*<[\s\S]*?wrote:\s*$/gm,
    /\s*On\s+[^<\n]*\s+at\s+[^<\n]*\s+[^<\n]*\s*wrote:[\s\S]*$/gm,
    
    // More specific patterns for the exact format we see
    /\s*On\s+[A-Z][a-z]{2},\s+[A-Z][a-z]{2}\s+\d{1,2},\s+\d{4}\s+at\s+\d{1,2}:\d{2}\s+[AP]M\s+.*<[\s\S]*?wrote:\s*$/gm,
    /\s*On\s+[A-Z][a-z]{2},\s+[A-Z][a-z]{2}\s+\d{1,2},\s+\d{4}\s+at\s+\d{1,2}:\d{2}\s+[AP]M\s+.*wrote:[\s\S]*$/gm,
    
    // Simpler fallback patterns
    /\n\n.*wrote:\s*$/gm,
    /\n.*<.*@.*>.*wrote:[\s\S]*$/gm,
    
    // Remove quoted content (lines starting with >)
    /\n>.*$/gm,
    
    // Remove "--- Original Message ---" and everything after
    /\s*---\s*Original Message\s*---[\s\S]*$/gm,
    
    // Remove "From:" headers and everything after
    /\s*From:[\s\S]*$/gm,
    
    // Remove dashed separators and everything after
    /\s*-----.*-----[\s\S]*$/gm,
    
    // German patterns
    /\s*Am\s+.*\s+schrieb.*:[\s\S]*$/gm
  ];
  
  let cleanContent = content;
  patterns.forEach(pattern => {
    cleanContent = cleanContent.replace(pattern, '');
  });
  
  return cleanContent.trim();
};

// Transform API comment to frontend comment format
export const transformApiCommentToFrontend = (apiComment: ApiComment, requesterId: string, isExternalTicket?: boolean, isInternalConversation?: boolean): Comment => {
  const authorName = `${apiComment.author_first_name} ${apiComment.author_last_name}`.trim();
  
  // Determine if this is a customer message (right side)
  let isCustomer: boolean;
  
  if (isExternalTicket) {
    // For external tickets: check user role
    // agent/admin = LEFT side, customer = RIGHT side
    const authorRole = apiComment.author_role || 'customer';
    isCustomer = authorRole === 'customer';
    
  } else if (isInternalConversation) {
    // Interne Konversation (org-içi): müşteri YOK — herkes iç taraf. Badge, author_role'e göre basılır.
    isCustomer = false;
  } else {
    // For normal tickets: if author is the requester, it's customer
    isCustomer = apiComment.author_id === requesterId;
    
  }
  
  // Debug attachment count (temporarily disabled)
  // if (apiComment.hasAttachments) {
  //   console.log(`Attachment count debug for comment ${apiComment.id}:`);
  //   console.log('- attachments_count:', apiComment.attachments_count);
  //   console.log('- attachmentCount:', apiComment.attachmentCount);
  //   console.log('- Final calculated count:', apiComment.attachments_count || apiComment.attachmentCount || 0);
  // }
  
  return {
    id: apiComment.id,
    content: cleanEmailContent(apiComment.content),
    author: {
      name: authorName || 'Unknown User',
      email: apiComment.author_email,
      avatar: generateAvatar(authorName || 'unknown')
    },
    timestamp: apiComment.created_at,
    isInternal: apiComment.is_internal,
    isEmailComment: apiComment.isEmailComment,
    emailSourceType: apiComment.emailSourceType || undefined,
    hasAttachments: apiComment.hasAttachments,
    attachmentCount: apiComment.attachments_count || apiComment.attachmentCount || 0,
    attachmentsUrl: apiComment.attachmentsUrl || undefined,
    authorRole: (apiComment as any).author_role || undefined,
    sender: isCustomer ? "customer" : "agent"
  };
};

// Statusbezeichnung — Schlüssel entsprechen exakt den API-Werten.
// Unbekannter Status gibt (wie bisher) den Rohwert zurück.
export const getStatusLabel = (status: string) => {
  switch (status) {
    case "new":
      return t("domain:status.new", "Neu");
    case "open":
      return t("domain:status.open", "Offen");
    case "pending":
      return t("domain:status.pending", "Wartend");
    case "in_progress":
      return t("domain:status.in_progress", "In Bearbeitung");
    case "closed":
      return t("domain:status.closed", "Geschlossen");
    case "solved":
      return t("domain:status.solved", "Gelöst");
    default:
      return status;
  }
};

// Get status color classes
export const getStatusColor = (status: string) => {
  switch (status) {
    case "new":
      return "bg-blue-100 text-blue-800";
    case "open":
      return "bg-green-100 text-green-800";
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "in_progress":
      return "bg-purple-100 text-purple-800";
    case "solved":
      return "bg-teal-100 text-teal-800";
    case "closed":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// Prioritätsbezeichnung
export const getPriorityLabel = (priority: string) => {
  switch (priority) {
    case "low":
      return t("domain:priority.low", "Niedrig");
    case "normal":
      return t("domain:priority.normal", "Normal");
    case "high":
      return t("domain:priority.high", "Hoch");
    case "urgent":
      return t("domain:priority.urgent", "Dringend");
    default:
      return priority;
  }
};

// Get priority color classes
export const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "low":
      return "bg-gray-100 text-gray-800";
    case "normal":
      return "bg-blue-100 text-blue-800";
    case "high":
      return "bg-orange-100 text-orange-800";
    case "urgent":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// SLA-Statusbezeichnung
export const getSlaStatusLabel = (status: "pending" | "achieved" | "breached") => {
  switch (status) {
    case "pending":
      return t("domain:sla.pending", "Wartend");
    case "achieved":
      return t("domain:sla.achieved", "Erreicht");
    case "breached":
      return t("domain:sla.breached", "Überschritten");
    default:
      return status;
  }
};

// Get SLA status color classes
export const getSlaStatusColor = (status: "pending" | "achieved" | "breached") => {
  switch (status) {
    case "pending":
      return "bg-orange-100 text-orange-800";
    case "achieved":
      return "bg-green-100 text-green-800";
    case "breached":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// Format time in minutes to human readable format
export const formatSlaTime = (minutes: number) => {
  if (minutes < 60) {
    return t("common:duration.minutes", "{{count}} Min.", { count: minutes });
  } else if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  } else {
    const days = Math.floor(minutes / 1440);
    const remainingHours = Math.floor((minutes % 1440) / 60);
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }
};
